import React, { useEffect, useState, useMemo, useRef  } from "react";
import { useCarrito } from "../context/CarritoContext";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/firebaseConfig";

export default function VistaCarrito({ cerrar, minimizar, estaMinimizado, ampliar }) {
  const {
    carrito,
    eliminarProducto,
    vaciarCarrito,
    aumentarCantidad,
    disminuirCantidad,
    cambiarModoVenta,
  } = useCarrito();
  const {setEstaMinimizado} = useCarrito();
  const [usuario, setUsuario] = useState(null);
  const navigate = useNavigate();
  const contenedorRef = useRef(null);
  const [width, setWidth] = useState(() => {
    // leer desde localStorage
    const stored = localStorage.getItem("vistaCarritoWidth");
    return stored ? stored : "400px"; // valor por defecto
  });

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (contenedorRef.current) {
        const nuevoAncho = contenedorRef.current.offsetWidth;
        localStorage.setItem("vistaCarritoWidth", `${nuevoAncho}px`);
      }
    });

    if (contenedorRef.current) {
      resizeObserver.observe(contenedorRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUsuario(u));
    return unsub;
  }, []);

  const totalProductos = useMemo(() => 
    carrito.reduce((sum, p) => sum + p.cantidad, 0), 
    [carrito]
  );

  // Si está minimizado, mostrar solo el botón de expandir
  if (estaMinimizado) {
    return (
      <div
        ref={contenedorRef}
        className="relative bg-gray-50 p-4 rounded-2xl border border-gray-300 shadow-lg ring-1 ring-sky-100 resize-x overflow-auto min-w-[300px] max-w-full"
        style={{ width }}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-[#40767b]">Tu Carrito ({totalProductos})</h3>
          <button
            onClick={minimizar}
            className="p-2 text-sky-600 hover:bg-sky-50 rounded-full"
            aria-label="Expandir carrito"
          >
            <svg 
              className="w-5 h-5" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M19 9l-7 7-7-7" 
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white p-4 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-sky-800">Tu Carrito ({totalProductos})</h3>
        <button
          onClick={minimizar}
          className="p-2 text-sky-600 hover:bg-sky-50 rounded-full"
          aria-label="Minimizar carrito"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12h12" />
          </svg>
        </button>
      </div>
  
      {carrito.length === 0 ? (
        <p className="text-sm text-gray-500">No has agregado productos.</p>
      ) : (
        <>
          <ul className="divide-y max-h-72 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-sky-400 scrollbar-track-gray-100">
            {carrito.map((item) => (
              <li key={item.sku} className="py-3 flex items-center gap-3">
                {item.imagen && (
                  <img src={item.imagen} alt={item.nombre} className="w-16 h-16 object-contain rounded-lg bg-white" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#40767b] truncate">
                    {item.nombre}
                    {item.modoVenta && (
                      <span className="ml-1 text-xs text-gray-500">({item.modoVenta})</span>
                    )}
                  </p>
                  
                  {item.modoFardo && (
                    <button
                      onClick={() => cambiarModoVenta(item.sku)}
                      className="text-xs text-sky-500 hover:underline mt-1"
                    >
                      Cambiar a {item.modoVenta === "fardo" ? "individual" : "fardo"}
                    </button>
                  )}
                  {item.descripcion && (
                    <p className="text-xs text-gray-500 truncate">{item.descripcion}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => disminuirCantidad(item.sku)}
                    disabled={item.cantidad <= 1}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-lg ${
                      item.cantidad <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-sky-50"
                    }`}
                  >
                    −
                  </button>
                
                  <input
                    type="number"
                    value={item.cantidad}
                    onChange={(e) => {
                      const nuevaCantidad = parseInt(e.target.value);
                      if (!isNaN(nuevaCantidad) && nuevaCantidad >= 1) {
                        aumentarCantidad(item.sku, nuevaCantidad); // admite cantidad directa
                      }
                    }}
                    className="w-16 text-center border border-gray-300 rounded-md text-sm px-1 py-0.5"
                    min={1}
                  />
                
                  <button
                    onClick={() => aumentarCantidad(item.sku)}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-lg hover:bg-sky-50"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => eliminarProducto(item.sku)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                  aria-label="Eliminar producto"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
  
          <div className="mt-4 text-right">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold">{totalProductos}</span>{" "}
              {totalProductos === 1 ? "producto" : "productos"}
            </p>
            <p className="text-xs italic text-gray-400 mt-1">
              Los precios se cotizan al finalizar
            </p>
          </div>
  
          <div className="flex gap-3 mt-4">
            <button
              onClick={vaciarCarrito}
              className="flex-1 py-2 text-sm border border-red-100 rounded-full hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-5 h-5" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.8} 
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                />
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.8} 
                  d="M12 10v4m0 0v4m0-4h4m-4 0H8" 
                  className="text-red-500"
                />
              </svg>
              Vaciar todo
            </button>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                navigate("/cotizar");
              }}
              className="flex-1 py-2 text-sm bg-[#66959a] text-white rounded-full hover:bg-[#40767b] flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Cotizar ahora
            </button>
          </div>
  
          <div className="mt-4 flex justify-between">
            <button
              onClick={cerrar}
              className="text-sm text-sky-600 hover:text-sky-800 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Seguir comprando
            </button>
            <button
              onClick={ampliar}
              className="text-sm text-sky-600 hover:text-sky-800 underline"
            >
              Vista detallada
            </button>
          </div>
        </>
      )}
    </div>
  );
}
